// CanvasGame.ts - 游戏主控制器 (重构版)
// 全面优化版：增强互动、改进UI、优化体验

import { Scene, GameObject } from './Scene.js';
import { FloatingTextSystem } from './FloatingText.js';
import { ParticleSystem } from './ParticleSystem.js';
import { AnimationSystem } from './Animation.js';
import { GameState, getGameState } from './GameState.js';
import { getSaveManager, SaveManager } from './SaveManager.js';
import { EventSystem, DialogueChoice, DialogueAction } from './EventSystem.js';
import { InventorySystem } from './ItemSystem.js';
import { AudioManager, playObjectSound } from './Audio.js';
import { NeighborSystem } from './NeighborSystem.js';
import { WeatherEventSystem, WEATHER_CONFIG, RANDOM_EVENTS } from './WeatherEventSystem.js';
import { CollectionSystem, CollectionCategory } from './CollectionSystem.js';
import { getRandomDialogue, DIALOGUES } from './DialogueData.js';

// 新系统
import { StorySystem, Task, MemoryFragment, GrowthStage, GROWTH_STAGES } from './StorySystem.js';
import { ActionSystem, ActionType, ActionTarget, HuanhuanState } from './ActionSystem.js';
import { IndoorScene, RoomType, IndoorObject, RoomConfig } from './IndoorScene.js';

// UI面板类型
export type UIPanel = 'main' | 'status' | 'inventory' | 'quest' | 'dialogue' | 'save' | 'settings' 
  | 'dailyQuests' | 'collection' | 'neighbor' | 'memoryQuests' | 'story' | 'actions' | 'indoor';

// 游戏场景类型
export type GameScene = 'yard' | RoomType;

// 动作栏按钮
interface ActionBarButton {
  id: string;
  icon: string;
  label: string;
  action: ActionType;
  enabled: boolean;
  cooldown: number;
  description: string;
}

// 交互菜单选项
interface InteractionOption {
  id: string;
  label: string;
  icon: string;
  description: string;
  action: () => void;
  enabled: boolean;
}

export class CanvasGame {
  protected canvas: HTMLCanvasElement;
  protected ctx: CanvasRenderingContext2D;
  protected scene: Scene;
  protected floatingText: FloatingTextSystem;
  protected particles: ParticleSystem;
  protected animations: AnimationSystem;
  protected audioManager: AudioManager;

  // 游戏系统
  protected gameState: GameState;
  protected saveManager: SaveManager;
  protected eventSystem: EventSystem;
  protected inventorySystem: InventorySystem;
  protected neighborSystem: NeighborSystem;
  protected weatherEventSystem: WeatherEventSystem;
  protected collectionSystem: CollectionSystem;

  // 新核心系统
  protected storySystem: StorySystem;
  protected actionSystem: ActionSystem;
  protected indoorScene: IndoorScene;

  // 场景状态
  protected currentGameScene: GameScene = 'yard';

  protected width: number = 0;
  protected height: number = 0;
  protected dpr: number = 1;
  protected isRunning: boolean = false;
  protected rafId: number = 0;

  // UI状态
  protected currentPanel: UIPanel = 'main';
  protected showDialogue: boolean = false;
  protected dialogueText: string = '';
  protected dialogueSpeaker: string = '';
  protected dialogueChoices: DialogueChoice[] = [];
  protected typingProgress: number = 0;
  protected targetText: string = '';

  // 增强的动作栏 - 带描述
  private actionBarButtons: ActionBarButton[] = [
    { id: 'feed', icon: '🌾', label: '喂食', action: 'feed', enabled: true, cooldown: 0, description: '喂养小动物' },
    { id: 'water', icon: '💧', label: '浇水', action: 'water', enabled: true, cooldown: 0, description: '给植物浇水' },
    { id: 'talk', icon: '💬', label: '对话', action: 'talk', enabled: true, cooldown: 0, description: '与角色交谈' },
    { id: 'sweep', icon: '🧹', label: '打扫', action: 'sweep', enabled: true, cooldown: 0, description: '清扫庭院' },
    { id: 'rest', icon: '😌', label: '休息', action: 'rest', enabled: true, cooldown: 0, description: '恢复体力' },
    { id: 'play', icon: '🎾', label: '玩耍', action: 'play', enabled: true, cooldown: 0, description: '一起玩耍' },
  ];
  private selectedAction: ActionType | null = null;
  private showActionBar: boolean = true;
  private hoveredActionButton: string | null = null;

  // 任务引导
  private showTaskGuide: boolean = true;
  private taskGuideHint: string = '点击地面移动，点击物体互动';

  // 选中物体
  private selectedObject: GameObject | IndoorObject | null = null;
  private selectedObjectPosition: { x: number; y: number } | null = null;
  
  // 增强的角色交互菜单
  private showInteractionMenu: boolean = false;
  private interactionMenuPosition: { x: number; y: number } = { x: 0, y: 0 };
  private interactionMenuOptions: InteractionOption[] = [];
  private interactionMenuTitle: string = '';
  private hoveredInteractionOption: string | null = null;

  // 回调
  public onCoinChange?: (coins: number) => void;
  public onPanelChange?: (panel: UIPanel) => void;

  // UI按钮 - 左侧边栏
  private leftSidebarButtons: Array<{
    id: string;
    icon: string;
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
    action: () => void;
    visible: boolean;
    description: string;
    badge?: number;
  }> = [];

  // 底部快捷栏
  private bottomQuickActions: Array<{
    id: string;
    icon: string;
    label: string;
    action: () => void;
    cooldown: number;
  }> = [];

  // 交互历史记录
  private recentInteractions: Array<{ type: string; target: string; time: number }> = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context');
    }
    this.ctx = ctx;

    // 初始化音频
    this.audioManager = new AudioManager();

    // 初始化游戏状态
    this.gameState = getGameState();
    this.saveManager = getSaveManager();
    this.eventSystem = new EventSystem();
    this.inventorySystem = new InventorySystem();
    this.neighborSystem = new NeighborSystem(this.gameState);
    this.weatherEventSystem = new WeatherEventSystem(this.gameState);
    this.collectionSystem = new CollectionSystem(this.gameState);

    // 初始化新系统
    this.storySystem = new StorySystem(this.gameState);
    this.actionSystem = new ActionSystem(this.gameState, this.storySystem);
    this.indoorScene = new IndoorScene();

    // 初始化渲染系统
    this.scene = new Scene();
    this.floatingText = new FloatingTextSystem();
    this.particles = new ParticleSystem();
    this.animations = new AnimationSystem();

    // 连接系统
    this.neighborSystem.setFloatingText(this.floatingText);
    this.weatherEventSystem.setFloatingText(this.floatingText);
    this.collectionSystem.setFloatingText(this.floatingText);
    this.actionSystem.setFloatingText(this.floatingText);

    // 设置尺寸
    this.resize();
    window.addEventListener('resize', () => this.resize());

    // 绑定输入事件
    this.bindInput();

    // 设置系统回调
    this.setupSystemCallbacks();

    // 初始化UI按钮
    this.initUIButtons();

    // 尝试加载自动存档
    this.tryLoadAutoSave();

    // 初始化任务提示
    this.lastHintChangeTime = Date.now();
    this.updateTaskGuide();
  }

  private setupSystemCallbacks(): void {
    // 故事系统回调
    this.storySystem.setCallbacks({
      onTaskUpdate: () => {
        this.updateTaskGuide();
      },
      onMemoryUnlock: (fragment) => {
        this.showFloatingText(this.width / 2, this.height / 3, `💭 解锁回忆：${fragment.title}`, 'achievement');
        this.audioManager.play('achievement');
      }
    });

    // 动作系统回调
    this.actionSystem.setCallbacks({
      onHuanhuanStateChange: (state) => {
        this.updateActionBarState(state);
      },
      onActionComplete: (result) => {
        if (result.success) {
          this.showFloatingText(this.width / 2, this.height / 2, result.message, 'info');
          // 记录交互历史
          this.recentInteractions.push({
            type: 'action',
            target: result.message,
            time: Date.now()
          });
          // 只保留最近10条
          if (this.recentInteractions.length > 10) {
            this.recentInteractions.shift();
          }
        }
      }
    });

    // 室内场景回调
    this.indoorScene.setCallbacks({
      onRoomChange: (room) => {
        console.log('进入房间:', room);
        this.showFloatingText(this.width / 2, this.height / 3, `🏠 进入${this.getRoomName(room)}`, 'info');
      },
      onObjectInteract: (obj, action) => {
        this.handleIndoorObjectInteract(obj, action);
      },
      onExit: (targetRoom) => {
        console.log('Exit clicked, target:', targetRoom);
        if (targetRoom === 'yard') {
          this.currentGameScene = 'yard';
          // 移除室内模式class
          const uiLayer = document.getElementById('ui-layer');
          if (uiLayer) uiLayer.classList.remove('indoor-mode');
          this.showFloatingText(this.width / 2, this.height / 2, '🏡 回到院子', 'info');
        } else {
          this.indoorScene.changeRoom(targetRoom);
        }
      },
      onMove: (x, y) => {
        // 移动时不做任何提示，静默移动
      }
    });

    // 事件系统回调
    this.eventSystem.setCallbacks({
      onTextDisplay: (text, speaker) => {
        this.showDialogueBox(text, speaker);
      },
      onChoicesDisplay: (choices) => {
        this.dialogueChoices = choices;
      },
      onAction: (action) => {
        this.handleDialogueAction(action);
      },
      onSceneChange: (sceneId) => {
        console.log('Scene changed to:', sceneId);
      }
    });

    // 天气事件回调
    this.weatherEventSystem.setCallbacks({
      onWeatherChange: (weather) => {
        console.log('Weather changed to:', weather);
      },
      onFestivalStart: (festival) => {
        console.log('Festival started:', festival);
      },
      onRandomEvent: (event) => {
        console.log('Random event:', event);
      }
    });
  }

  private getRoomName(room: RoomType): string {
    const roomNames: Record<RoomType, string> = {
      'living_room': '客厅',
      'kitchen': '厨房',
      'grandma_room': '奶奶的房间',
      'daye_room': '爷爷的房间',
      'storage_room': '储藏室',
      'bedroom': '欢欢的房间',
      'bathroom': '卫生间',
      'study_room': '书房',
      'attic': '阁楼'
    };
    return roomNames[room] || '房间';
  }

  private startFirstTask(): void {
    const currentTask = this.storySystem.getCurrentTask();
    if (currentTask && currentTask.status === 'available') {
      this.storySystem.acceptTask(currentTask.id);
      this.updateTaskGuide();
    }
  }

  // 任务提示 - 显示简单的可操作提示
  private taskGuideHints: string[] = [
    '💡 可以找奶奶聊聊天',
    '💡 可以逗丹丹玩',
    '💡 可以浇浇花',
    '💡 可以找爷爷说说话',
    '💡 点击房子可以进屋',
    '💡 点击地面可以走动',
    '💡 和丹丹玩耍会增加快乐值',
    '💡 帮奶奶做家务会得到夸奖',
    '💡 休息可以恢复体力',
    '💡 收集回忆可以解锁故事'
  ];
  private currentHintIndex: number = 0;
  private lastHintChangeTime: number = 0;

  private updateTaskGuide(): void {
    // 每5秒切换一次提示
    const now = Date.now();
    if (now - this.lastHintChangeTime > 5000) {
      this.currentHintIndex = (this.currentHintIndex + 1) % this.taskGuideHints.length;
      this.lastHintChangeTime = now;
    }
    this.taskGuideHint = this.taskGuideHints[this.currentHintIndex];
  }

  private updateActionBarState(state: HuanhuanState): void {
    // 根据能量更新动作栏可用状态
    this.actionBarButtons.forEach(btn => {
      switch (btn.action) {
        case 'sweep':
        case 'chop_wood':
          btn.enabled = state.energy >= 20;
          break;
        case 'water':
        case 'tidy':
        case 'play':
          btn.enabled = state.energy >= 15;
          break;
        case 'feed':
        case 'talk':
          btn.enabled = state.energy >= 10;
          break;
        case 'rest':
          btn.enabled = state.energy < 100;
          break;
      }
    });
  }

  private initUIButtons(): void {
    const btnSize = Math.min(50, this.width / 12);
    const gap = 10;
    const startY = 70;

    this.leftSidebarButtons = [
      {
        id: 'status',
        icon: '👤',
        label: '状态',
        x: 0, y: startY, width: btnSize, height: btnSize,
        action: () => this.openPanel('status'),
        visible: true,
        description: '查看角色状态',
      },
      {
        id: 'story',
        icon: '📖',
        label: '故事',
        x: 0, y: startY + btnSize + gap, width: btnSize, height: btnSize,
        action: () => this.openPanel('story'),
        visible: true,
        description: '任务与剧情',
      },
      {
        id: 'collection',
        icon: '📚',
        label: '回忆',
        x: 0, y: startY + (btnSize + gap) * 2, width: btnSize, height: btnSize,
        action: () => this.openPanel('collection'),
        visible: true,
        description: '收集的回忆',
      },
      {
        id: 'neighbor',
        icon: '🏘️',
        label: '邻居',
        x: 0, y: startY + (btnSize + gap) * 3, width: btnSize, height: btnSize,
        action: () => this.openPanel('neighbor'),
        visible: true,
        description: '邻居互动',
      },
      {
        id: 'save',
        icon: '💾',
        label: '保存',
        x: 0, y: startY + (btnSize + gap) * 4, width: btnSize, height: btnSize,
        action: () => {
          this.saveGame();
          this.showFloatingText(this.width / 2, this.height / 2, '游戏进度已保存', 'info');
        },
        visible: true,
        description: '保存游戏',
      },
    ];
  }

  private updateUIButtonPositions(): void {
    const isMobile = this.width < 600;
    const btnSize = isMobile ? Math.min(36, this.width / 10) : Math.min(50, this.width / 12);
    const gap = isMobile ? 6 : 10;
    const leftX = isMobile ? 5 : this.width * 0.03;
    const startY = isMobile ? 55 : this.height * 0.12;

    this.leftSidebarButtons.forEach((btn, index) => {
      btn.x = leftX;
      btn.y = startY + index * (btnSize + gap);
      btn.width = btnSize;
      btn.height = btnSize;
    });
  }

  private tryLoadAutoSave(): void {
    const saveData = this.saveManager.quickLoad();
    if (saveData) {
      this.gameState.import(saveData);
      const anySave = saveData as any;
      if (anySave.inventory) this.inventorySystem.import(anySave.inventory);
      if (anySave.neighborSystem) this.neighborSystem.import(anySave.neighborSystem);
      if (anySave.weatherEventSystem) this.weatherEventSystem.import(anySave.weatherEventSystem);
      if (anySave.collectionSystem) this.collectionSystem.import(anySave.collectionSystem);
      if (anySave.currentScene) this.currentGameScene = anySave.currentScene;
      console.log('✅ 自动存档已加载');
    } else {
      this.inventorySystem.addCurrency(100);
      console.log('🆕 开始新游戏');
    }
  }

  protected resize(): void {
    // 正常清晰渲染
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = window.innerWidth;
    this.height = window.innerHeight;

    this.canvas.width = this.width * this.dpr;
    this.canvas.height = this.height * this.dpr;
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;

    this.ctx.scale(this.dpr, this.dpr);

    this.scene.setSize(this.width, this.height);
    this.indoorScene.setSize(this.width, this.height);
    this.updateUIButtonPositions();
  }

  private bindInput(): void {
    // 鼠标移动事件 - 用于悬停效果
    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      this.handleMouseMove(x, y);
    });

    const handlePointerDown = (e: PointerEvent | Touch) => {
      console.log('CLICK! Scene:', this.currentGameScene);
      const rect = this.canvas.getBoundingClientRect();
      const x = ('clientX' in e ? e.clientX : 0) - rect.left;
      const y = ('clientY' in e ? e.clientY : 0) - rect.top;

      // 检查是否点击了动作栏
      if (this.showActionBar && this.currentPanel === 'main') {
        const actionBarResult = this.handleActionBarClick(x, y);
        if (actionBarResult) return;
      }

      // 检查是否点击了左侧UI按钮
      if (this.currentPanel === 'main') {
        for (const btn of this.leftSidebarButtons) {
          if (btn.visible &&
              x >= btn.x && x <= btn.x + btn.width &&
              y >= btn.y && y <= btn.y + btn.height) {
            btn.action();
            this.playClickSound();
            return;
          }
        }
      }

      // 处理面板点击
      if (this.currentPanel !== 'main') {
        if (this.currentPanel === 'dialogue') {
          this.handleDialogueClick(x, y);
        } else {
          this.closePanel();
        }
        return;
      }

      // 检查交互菜单点击
      if (this.showInteractionMenu) {
        const menuClicked = this.handleInteractionMenuClick(x, y);
        if (menuClicked) return;
        this.hideInteractionMenu();
      }

      // 根据当前场景处理点击
      console.log('Click! Scene:', this.currentGameScene);
      if (this.currentGameScene === 'yard') {
        this.handleYardClick(x, y);
      } else {
        console.log('Calling indoor handleClick');
        this.indoorScene.handleClick(x, y);
      }
    };

    this.canvas.addEventListener('pointerdown', handlePointerDown);
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      handlePointerDown(touch);
    }, { passive: false });
    
    // 禁止右键菜单
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private handleMouseMove(x: number, y: number): void {
    // 检查动作栏悬停
    this.hoveredActionButton = null;
    if (this.showActionBar && this.currentPanel === 'main') {
      const barY = this.height - 80;
      const btnWidth = 60;
      const btnHeight = 50;
      const gap = 10;
      const startX = (this.width - (this.actionBarButtons.length * (btnWidth + gap) - gap)) / 2;

      for (let i = 0; i < this.actionBarButtons.length; i++) {
        const btn = this.actionBarButtons[i];
        const btnX = startX + i * (btnWidth + gap);
        if (x >= btnX && x <= btnX + btnWidth && y >= barY && y <= barY + btnHeight) {
          this.hoveredActionButton = btn.id;
          return;
        }
      }
    }

    // 检查交互菜单悬停
    this.hoveredInteractionOption = null;
    if (this.showInteractionMenu) {
      const menuX = this.interactionMenuPosition.x;
      const menuY = this.interactionMenuPosition.y;
      const menuWidth = 140;
      const itemHeight = 40;
      
      for (let i = 0; i < this.interactionMenuOptions.length; i++) {
        const itemY = menuY + 50 + i * itemHeight;
        if (x >= menuX - menuWidth/2 && x <= menuX + menuWidth/2 &&
            y >= itemY && y <= itemY + itemHeight) {
          this.hoveredInteractionOption = this.interactionMenuOptions[i].id;
          return;
        }
      }
    }
  }

  private handleInteractionMenuClick(x: number, y: number): boolean {
    const menuX = this.interactionMenuPosition.x;
    const menuY = this.interactionMenuPosition.y;
    const menuWidth = 140;
    const itemHeight = 40;
    
    // 检查关闭按钮
    if (x >= menuX + menuWidth/2 - 25 && x <= menuX + menuWidth/2 - 5 &&
        y >= menuY + 10 && y <= menuY + 30) {
      this.hideInteractionMenu();
      return true;
    }
    
    // 检查选项点击
    for (let i = 0; i < this.interactionMenuOptions.length; i++) {
      const option = this.interactionMenuOptions[i];
      const itemY = menuY + 50 + i * itemHeight;
      if (x >= menuX - menuWidth/2 && x <= menuX + menuWidth/2 &&
          y >= itemY && y <= itemY + itemHeight) {
        if (option.enabled) {
          option.action();
          this.hideInteractionMenu();
          this.playClickSound();
        }
        return true;
      }
    }
    return false;
  }

  private handleActionBarClick(x: number, y: number): boolean {
    const barY = this.height - 80;
    const btnWidth = 60;
    const btnHeight = 50;
    const gap = 10;
    const startX = (this.width - (this.actionBarButtons.length * (btnWidth + gap) - gap)) / 2;

    for (let i = 0; i < this.actionBarButtons.length; i++) {
      const btn = this.actionBarButtons[i];
      const btnX = startX + i * (btnWidth + gap);
      
      if (x >= btnX && x <= btnX + btnWidth && y >= barY && y <= barY + btnHeight) {
        if (btn.enabled) {
          // 切换选中状态
          if (this.selectedAction === btn.action) {
            this.selectedAction = null; // 取消选择
            this.showFloatingText(x, y - 30, '取消选择', 'info');
          } else {
            this.selectedAction = btn.action;
            this.playClickSound();
            this.showFloatingText(x, y - 30, `选择：${btn.label}`, 'info');
          }
        } else {
          this.showFloatingText(x, y - 30, '能量不足，先休息一下', 'cooldown');
        }
        return true;
      }
    }
    return false;
  }

  protected handleYardClick(x: number, y: number): void {
    // 调试输出
    console.log('🖱️ Yard clicked at:', x, y);
    
    const clickedObject = this.scene.getObjectAt(x, y);

    if (clickedObject) {
      console.log('✅ Object clicked:', clickedObject.type, clickedObject.id);
      this.handleObjectClick(clickedObject, x, y);
    } else {
      console.log('❌ No object at click position');
      // 如果选中了动作，执行动作
      if (this.selectedAction) {
        const target: ActionTarget = {
          type: 'ground',
          id: 'ground',
          label: '地面',
          x, y
        };
        this.actionSystem.executeAction(this.selectedAction, target);
        this.selectedAction = null;
        this.showFloatingText(x, y - 30, '👣 走向目的地...', 'info');
      } else {
        // 移动欢欢
        this.scene.moveHuanhuanTo(x, y);
        this.storySystem.checkAndUpdateObjective('complete_action', 'move');
      }
    }
  }

  protected handleObjectClick(obj: GameObject, x: number, y: number): void {
    console.log('🎯 handleObjectClick:', obj.type, obj.id);
    
    // 播放点击音效
    playObjectSound(obj.type);

    // 检查是否是房子或门口 - 切换场景
    if (obj.type === 'main_house' || obj.type === 'door') {
      console.log('🏠 Entering house...');
      this.currentGameScene = 'living_room';
      // 添加室内模式class
      const uiLayer = document.getElementById('ui-layer');
      if (uiLayer) uiLayer.classList.add('indoor-mode');
      this.indoorScene.changeRoom('living_room');
      this.showFloatingText(x, y - 40, '🏠 回到家中', 'info');
      this.actionSystem.executeAction('enter_house');
      return;
    }

    if (obj.type === 'gate') {
      console.log('🚪 Clicked gate');
      this.showFloatingText(x, y - 40, '🚪 大门，出去就是村道', 'info');
      return;
    }

    // 检查是否是选中动作的目标
    if (this.selectedAction) {
      const target: ActionTarget = {
        type: obj.type,
        id: obj.id,
        label: this.getObjectLabel(obj.type),
        x, y
      };
      const result = this.actionSystem.executeAction(this.selectedAction, target);
      if (result.success) {
        this.selectedAction = null;
        this.showFloatingText(x, y - 40, result.message, 'info');
        return;
      }
    }

    // 显示增强的交互菜单
    this.showEnhancedInteractionMenu(obj, x, y);
  }

  // 显示增强的交互菜单
  private showEnhancedInteractionMenu(obj: GameObject, x: number, y: number): void {
    this.interactionMenuPosition = { x, y };
    this.interactionMenuOptions = [];
    
    // 根据对象类型生成交互选项
    const availableActions = this.actionSystem.getAvailableActions(obj.type, obj.id);
    const objLabel = this.getObjectLabel(obj.type);
    this.interactionMenuTitle = objLabel;
    
    // 为每个可用动作创建选项
    availableActions.forEach(actionType => {
      const option = this.createInteractionOption(actionType, obj, x, y);
      if (option) {
        this.interactionMenuOptions.push(option);
      }
    });
    
    // 添加查看选项
    this.interactionMenuOptions.push({
      id: 'look',
      label: '查看',
      icon: '👀',
      description: `查看${objLabel}`,
      action: () => {
        const lookTexts = this.getObjectLookTexts(obj.type);
        const text = lookTexts[Math.floor(Math.random() * lookTexts.length)];
        this.showFloatingText(x, y - 50, text, 'info');
      },
      enabled: true
    });
    
    this.showInteractionMenu = true;
    console.log('👤 Interaction menu shown:', obj.type, 'Options:', this.interactionMenuOptions.length);
  }

  private createInteractionOption(actionType: ActionType, obj: GameObject, x: number, y: number): InteractionOption | null {
    const huanhuanState = this.actionSystem.getHuanhuanState();
    
    switch (actionType) {
      case 'talk':
        return {
          id: 'talk',
          label: '对话',
          icon: '💬',
          description: `与${this.getObjectLabel(obj.type)}交谈`,
          action: () => this.handleCharacterTalk(obj, x, y),
          enabled: huanhuanState.energy >= 10
        };
      case 'feed':
        return {
          id: 'feed',
          label: '喂食',
          icon: '🌾',
          description: '给动物喂食',
          action: () => this.handleFeedAction(obj, x, y),
          enabled: huanhuanState.energy >= 10
        };
      case 'play':
        return {
          id: 'play',
          label: '玩耍',
          icon: '🎾',
          description: '一起玩耍',
          action: () => this.handlePlayAction(obj, x, y),
          enabled: huanhuanState.energy >= 15
        };
      case 'water':
        return {
          id: 'water',
          label: '浇水',
          icon: '💧',
          description: '给植物浇水',
          action: () => this.handleWaterAction(obj, x, y),
          enabled: huanhuanState.energy >= 15
        };
      case 'help':
        return {
          id: 'help',
          label: '帮忙',
          icon: '🤝',
          description: '帮助做家务',
          action: () => this.handleHelpAction(obj, x, y),
          enabled: huanhuanState.energy >= 15
        };
      case 'rest':
        return {
          id: 'rest',
          label: '休息',
          icon: '😌',
          description: '在此休息',
          action: () => this.handleRestAction(obj, x, y),
          enabled: huanhuanState.energy < 100
        };
      case 'pet':
        return {
          id: 'pet',
          label: '抚摸',
          icon: '🤗',
          description: '轻轻抚摸',
          action: () => this.handlePetAction(obj, x, y),
          enabled: true
        };
      default:
        return null;
    }
  }

  private handleCharacterTalk(obj: GameObject, x: number, y: number): void {
    const dialogues = this.getCharacterDialogues(obj.type);
    const dialogue = dialogues[Math.floor(Math.random() * dialogues.length)];
    
    this.showFloatingText(x, y - 50, dialogue, 'info');
    
    // 增加亲密度
    if (obj.type === 'dandan') {
      this.gameState.addToDandanAttribute('intimacy', 2);
      this.scene.makeDandanHappy();
    }
    
    // 记录交互
    this.recentInteractions.push({
      type: 'talk',
      target: obj.type,
      time: Date.now()
    });
    
    this.particles.spawnBurst(x, y, 'heart');
    this.audioManager.play('coin');
  }

  private handleFeedAction(obj: GameObject, x: number, y: number): void {
    const messages: Record<string, string> = {
      'chicken': '🐔 撒下一把小米，小鸡们开心地吃着~',
      'chicken_coop': '🐔 给母鸡撒了把谷子，它咯咯叫着表示感谢',
      'dandan': '🦴 给丹丹一根骨头，它开心地啃着~',
      'cat_pet': '🐟 给猫咪一条小鱼，它优雅地品尝着~'
    };
    
    const message = messages[obj.type] || '喂食完成';
    this.showFloatingText(x, y - 50, message, 'info');
    this.particles.spawnBurst(x, y, 'heart');
    this.audioManager.play('coin');
  }

  private handlePlayAction(obj: GameObject, x: number, y: number): void {
    if (obj.type === 'dandan') {
      const playMessages = [
        '🎾 和丹丹玩抛接球，它跑得真快！',
        '🦴 丹丹叼着骨头开心地摇尾巴~',
        '🐕 追蝴蝶玩，丹丹好灵活！',
        '🤗 和丹丹追逐打闹，好开心！'
      ];
      const msg = playMessages[Math.floor(Math.random() * playMessages.length)];
      this.showFloatingText(x, y - 50, msg, 'info');
      this.scene.makeDandanHappy();
    } else {
      this.showFloatingText(x, y - 50, '玩得很开心！', 'info');
    }
    this.particles.spawnBurst(x, y, 'dandan');
    this.audioManager.play('coin');
  }

  private handleWaterAction(obj: GameObject, x: number, y: number): void {
    this.showFloatingText(x, y - 50, '💧 浇过水的花草更加精神了~', 'info');
    this.particles.spawnBurst(x, y, 'water');
    this.audioManager.play('coin');
  }

  private handleHelpAction(obj: GameObject, x: number, y: number): void {
    const helpMessages: Record<string, string> = {
      'grandma': '👵 帮奶奶做了些家务，奶奶夸你真懂事~',
      'daye': '👴 帮爷爷搬东西，爷爷露出欣慰的笑容~'
    };
    const msg = helpMessages[obj.type] || '帮助完成';
    this.showFloatingText(x, y - 50, msg, 'info');
    this.particles.spawnBurst(x, y, 'heart');
    this.audioManager.play('coin');
  }

  private handleRestAction(obj: GameObject, x: number, y: number): void {
    this.showFloatingText(x, y - 50, '😌 休息了一会儿，感觉精神多了~', 'info');
    this.audioManager.play('coin');
  }

  private handlePetAction(obj: GameObject, x: number, y: number): void {
    if (obj.type === 'dandan') {
      this.showFloatingText(x, y - 50, '🐕 轻轻抚摸丹丹，它舒服地眯起眼睛~', 'info');
      this.scene.makeDandanHappy();
    } else if (obj.type === 'cat_pet') {
      this.showFloatingText(x, y - 50, '🐱 猫咪发出呼噜呼噜的声音~', 'info');
    } else {
      this.showFloatingText(x, y - 50, '🤗 轻轻抚摸', 'info');
    }
    this.particles.spawnBurst(x, y, 'heart');
    this.audioManager.play('coin');
  }

  private getCharacterDialogues(type: string): string[] {
    const dialogues: Record<string, string[]> = {
      'grandma': [
        '奶奶笑着说："欢欢来啦，饿不饿？"',
        '奶奶："今天天气真好，出去玩玩吧~"',
        '奶奶摸摸你的头："真乖~"',
        '奶奶："记得按时吃饭，别饿着了"',
        '奶奶："晚上想吃什么？奶奶给你做"',
        '奶奶："丹丹又跑哪去了？看着点它"'
      ],
      'daye': [
        '爷爷说："欢欢又长高了！"',
        '爷爷："来，爷爷给你讲个故事~"',
        '爷爷笑着说："好孩子！"',
        '爷爷："外面玩得开心吗？"',
        '爷爷："早起三光，晚起三慌"',
        '爷爷："这地啊，就得勤打理"'
      ],
      'dandan': [
        '丹丹：汪汪！（摇尾巴）',
        '丹丹歪着头看着你~',
        '丹丹开心地跳来跳去！',
        '丹丹舔了舔你的手~',
        '丹丹蹭蹭你的腿~'
      ],
      'neighbor': [
        '邻居笑眯眯地说："欢欢好！"',
        '邻居："听说你家丹丹生了三只小狗？"',
        '邻居："借点酱油，晚上做红烧肉"',
        '邻居："集市上的鸡蛋又涨价了"',
        '邻居："你家菜地的黄瓜长得真好"'
      ]
    };
    return dialogues[type] || ['你们聊了一会儿天...'];
  }

  private getObjectLookTexts(type: string): string[] {
    const texts: Record<string, string[]> = {
      'tree': ['🌳 这棵老槐树，奶奶说她小时候就有了', '🌳 树荫下真凉快', '🌳 树上几只小鸟在唱歌'],
      'well': ['💧 井水清凉甘甜', '💧 辘轳吱呀作响', '💧 井水深不见底'],
      'bamboo_chair': ['🪑 竹椅吱呀作响', '🪑 坐上去很凉快', '🪑 奶奶最喜欢的椅子'],
      'vegetable_patch': ['🥬 蔬菜长得真好', '🥬 该浇水了', '🥬 有蝴蝶在飞'],
      'chicken_coop': ['🐔 母鸡咕咕叫着', '🐔 好像在要吃的', '🐔 鸡窝里有几个蛋'],
      'main_house': ['🏠 温暖的家', '🏠 点击进屋', '🏠 家是最温暖的地方'],
      'grandma': ['👵 奶奶慈祥地看着你', '👵 奶奶正在休息', '👵 奶奶在扇扇子'],
      'daye': ['👴 爷爷抽着旱烟', '👴 爷爷在看报纸', '👴 爷爷在思考'],
      'dandan': ['🐕 丹丹摇着尾巴', '🐕 丹丹看起来很开心', '🐕 丹丹想和你玩'],
      'cat_pet': ['🐱 猫咪懒洋洋地', '🐱 猫咪在打盹', '🐱 猫咪优雅地舔着爪子'],
      'gate': ['🚪 大门', '🚪 出去就是村道', '🚪 邻居们经常从这里经过']
    };
    return texts[type] || ['你仔细看了看...'];
  }

  // 隐藏交互菜单
  private hideInteractionMenu(): void {
    this.showInteractionMenu = false;
    this.interactionMenuOptions = [];
    this.hoveredInteractionOption = null;
  }

  private handleIndoorObjectInteract(obj: IndoorObject, action: string): void {
    console.log('🏠 Indoor object interact:', obj.name, obj.type, action);
    
    const messages: Record<string, string[]> = {
      'sofa': ['坐在沙发上休息了一会儿~', '沙发很软很舒服', '打了个小盹'],
      'tea_table': ['茶几上放着茶杯和果盘', '杯子很精致', '有水果可以吃'],
      'tv': ['电视机里正在播放动画片~', '是欢欢最喜欢的节目', '画面有些雪花'],
      'cabinet': ['翻找了一下，发现了一些老照片', '里面有很多回忆', '柜子擦得很干净'],
      'kang_bed': ['火炕真暖和~', '冬天睡在这里特别舒服', '奶奶铺的褥子很软'],
      'stove': ['奶奶正在做饭，好香啊~', '锅里炖着红烧肉', '火候正好'],
      'bookshelf': ['书架上有好多旧书', '有爷爷的字帖', '还有爸爸的旧课本'],
      'photo_frame': ['看着照片，想起了好多往事...', '这是全家福', '照片已经泛黄了'],
      'dresser': ['梳妆台上放着奶奶常用的梳子', '还有一面小镜子', '香粉盒很精致'],
      'wardrobe': ['衣柜里整齐地叠着衣服', '有奶奶做的棉袄', '还有欢欢的新衣服'],
      'mirror': ['镜子里映出自己的模样', '你笑了笑', '镜子有些斑驳'],
      'grandpa_bed': ['木床很结实', '爷爷铺的床单很干净', '被子有阳光的味道'],
      'desk': ['书桌上放着爷爷的眼镜', '还有一本翻开的书', '笔筒里插着几支毛笔'],
      'lamp': ['煤油灯发出温暖的光', '灯芯需要修剪了', '在灯光下看书很温馨'],
      'opium_pipe': ['爷爷的旱烟袋用了几十年了', '已经摩得发亮', '是爷爷的宝贝'],
      'newspaper_stack': ['一摞旧报纸', '有去年的新闻', '爷爷喜欢收集报纸'],
      'cutting_board': ['砧板上放着菜刀', '还有切好的蔬菜', '奶奶刀工很好'],
      'pot': ['大铁锅能煮好多饭', '锅里的汤在翻滚', '奶奶做的饭最香'],
      'sink': ['水缸里装满了清水', '是从井里打来的', '水很清凉'],
      'rice_cooker': ['米缸里装着新米', '是今年的新米', '能闻到米香'],
      'thermos': ['暖水瓶里装着热水', '保温效果很好', '晚上可以泡脚']
    };

    const messageList = messages[obj.type] || [obj.description || `你查看了${obj.name}`];
    const message = messageList[Math.floor(Math.random() * messageList.length)];
    this.showFloatingText(this.width / 2, this.height / 2, message, 'info');

    // 执行对应动作
    const target: ActionTarget = {
      type: obj.type,
      id: obj.id,
      label: obj.name,
      x: obj.x, y: obj.y
    };

    if (action === 'rest' || obj.type === 'sofa' || obj.type === 'kang_bed' || obj.type === 'grandpa_bed') {
      this.actionSystem.executeAction('rest');
    } else if (action === 'look') {
      this.storySystem.checkAndUpdateObjective('complete_action', 'look');
    }
    
    // 播放音效
    this.audioManager.play('coin');
  }

  private getObjectLabel(type: string): string {
    const labels: Record<string, string> = {
      'grandma': '奶奶',
      'daye': '爷爷',
      'dandan': '丹丹',
      'neighbor': '邻居',
      'chicken_coop': '鸡窝',
      'vegetable_patch': '菜园',
      'flower': '花丛',
      'tree': '老槐树',
      'well': '水井',
      'bamboo_chair': '竹椅',
      'wood_pile': '柴堆',
      'broom': '扫帚',
      'main_house': '房子',
      'gate': '大门',
      'cat_pet': '猫咪'
    };
    return labels[type] || '物体';
  }

  private handleGrandmaInteraction(): void {
    const dialogue = getRandomDialogue('grandma');
    if (dialogue) {
      this.showFloatingText(this.width / 2, this.height / 2, `${dialogue.speaker}："${dialogue.text}"`, 'info');
    }
  }

  private handleDandanInteraction(): void {
    const intimacy = this.gameState.getPath('dandan.intimacy');
    let condition = 'intimacy:medium';
    if (intimacy < 200) condition = 'intimacy:low';
    else if (intimacy > 700) condition = 'intimacy:high';
    
    const dialogue = getRandomDialogue('dandan', condition);
    if (dialogue) {
      this.showFloatingText(this.width / 2, this.height / 2, dialogue.text, 'info');
    }
    this.gameState.addToDandanAttribute('intimacy', 5);
    this.showFloatingText(this.width / 2, this.height / 2 - 30, '亲密度 +5', 'exp');
    this.audioManager.play('dog');
  }

  private handleDayeInteraction(): void {
    const dialogue = getRandomDialogue('daye');
    if (dialogue) {
      this.showFloatingText(this.width / 2, this.height / 2, `${dialogue.speaker}："${dialogue.text}"`, 'info');
    }
  }

  private handleNeighborInteraction(): void {
    if (this.neighborSystem.isNeighborVisiting()) {
      this.currentPanel = 'neighbor';
      this.showDialogue = true;
      return;
    }

    const texts = [
      '邻居：欢欢又长高了！',
      '邻居：奶奶在家吗？',
      '邻居：给你们带了点蔬菜~',
      '邻居：今天天气真好！'
    ];
    this.showFloatingText(this.width / 2, this.height / 2, texts[Math.floor(Math.random() * texts.length)], 'info');
  }

  private showDialogueBox(text: string, speaker?: string): void {
    this.targetText = text;
    this.dialogueText = '';
    this.typingProgress = 0;
    this.dialogueSpeaker = speaker || '';
  }

  private handleDialogueClick(_x: number, y: number): void {
    const boxH = 200;
    const optionStartY = this.height - boxH + 80;

    if (this.dialogueChoices.length > 0 && this.typingProgress >= this.targetText.length) {
      this.dialogueChoices.forEach((_choice, i) => {
        const optionY = optionStartY + i * 35;
        if (y >= optionY - 15 && y <= optionY + 5) {
          this.closePanel();
          this.playClickSound();
        }
      });
    } else if (this.typingProgress < this.targetText.length) {
      this.typingProgress = this.targetText.length;
      this.playClickSound();
    } else {
      this.closePanel();
      this.playClickSound();
    }
  }

  private handleDialogueAction(action: DialogueAction): void {
    switch (action.type) {
      case 'setFlag':
        this.gameState.setFlag(action.params.flag, action.params.value);
        break;
      case 'giveItem':
        this.inventorySystem.addItem(action.params.itemId, action.params.quantity || 1);
        break;
      case 'removeItem':
        this.inventorySystem.removeItem(action.params.itemId, action.params.quantity || 1);
        break;
      case 'modifyAttribute':
        const attr = action.params.attr;
        if (attr.startsWith('dandan.')) {
          const dandanAttr = attr.replace('dandan.', '') as any;
          this.gameState.addToDandanAttribute(dandanAttr, action.params.amount);
        } else {
          this.gameState.addToAttribute(attr as any, action.params.amount);
        }
        break;
      case 'startQuest':
        this.storySystem.acceptTask(action.params.questId);
        break;
      case 'playSound':
        this.audioManager.play(action.params.sound);
        break;
    }
  }

  private playClickSound(): void {
    this.audioManager.play('coin');
  }

  protected showFloatingText(x: number, y: number, text: string, type: 'coin' | 'exp' | 'cooldown' | 'info' | 'achievement' = 'info'): void {
    this.floatingText.spawn(x, y, text, type);
  }

  public openPanel(panel: UIPanel): void {
    this.currentPanel = panel;
    if (this.onPanelChange) {
      this.onPanelChange(panel);
    }
  }

  public closePanel(): void {
    if (this.currentPanel === 'dialogue') {
      this.showDialogue = false;
    }
    this.currentPanel = 'main';
    if (this.onPanelChange) {
      this.onPanelChange('main');
    }
  }

  public saveGame(slotId?: number): boolean {
    const saveData = this.gameState.export();
    (saveData as any).inventory = this.inventorySystem.export();
    (saveData as any).neighborSystem = this.neighborSystem.export();
    (saveData as any).weatherEventSystem = this.weatherEventSystem.export();
    (saveData as any).collectionSystem = this.collectionSystem.export();
    (saveData as any).currentScene = this.currentGameScene;

    if (slotId !== undefined) {
      return this.saveManager.save(slotId, saveData);
    } else {
      return this.saveManager.quickSave(saveData);
    }
  }

  public start(): void {
    this.isRunning = true;
    this.loop();

    this.saveManager.startAutosave(() => {
      const saveData = this.gameState.export();
      (saveData as any).inventory = this.inventorySystem.export();
      (saveData as any).neighborSystem = this.neighborSystem.export();
      (saveData as any).weatherEventSystem = this.weatherEventSystem.export();
      (saveData as any).collectionSystem = this.collectionSystem.export();
      (saveData as any).currentScene = this.currentGameScene;
      return saveData;
    });
  }

  public stop(): void {
    this.isRunning = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    this.saveManager.stopAutosave();
  }

  private loop(): void {
    if (!this.isRunning) return;
    const now = performance.now();
    const dt = this.lastTime ? (now - this.lastTime) / 1000 : 1/60;
    this.lastTime = now;
    this.update(dt);
    this.render();
    this.rafId = requestAnimationFrame(() => this.loop());
  }

  private lastTime: number = 0;

  protected update(dt: number): void {

    // 打字机效果
    if (this.typingProgress < this.targetText.length) {
      this.typingProgress += 0.5;
      this.dialogueText = this.targetText.slice(0, Math.floor(this.typingProgress));
    }

    // 更新场景
    if (this.currentGameScene === 'yard') {
      this.scene.update(dt);
    } else {
      // 室内场景更新
      this.indoorScene.update(dt);
    }
    
    this.floatingText.update(dt);
    this.particles.update(dt);
    this.animations.update(dt);
    this.weatherEventSystem.update(dt);

    // 检查邻居来访
    if (this.neighborSystem.shouldVisit()) {
      const neighbor = this.neighborSystem.startVisit();
      if (neighbor) {
        this.showFloatingText(this.width / 2, this.height / 4, `${neighbor.avatar} ${neighbor.name}来访啦！`, 'achievement');
      }
    }
  }

  private render(): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.width, this.height);

    // 渲染场景
    if (this.currentGameScene === 'yard') {
      this.scene.render(ctx);
      this.weatherEventSystem.renderWeather(ctx);
    } else {
      // 室内场景：绘制在画布中央
      const roomWidth = 600;
      const roomHeight = 400;
      const x = (this.width - roomWidth) / 2;
      const y = (this.height - roomHeight) / 2;
      ctx.save();
      ctx.translate(x, y);
      this.indoorScene.render(ctx);
      ctx.restore();
    }

    // 渲染粒子
    this.particles.render(ctx);

    // 渲染飘字
    this.floatingText.render(ctx);

    // 渲染UI
    this.renderUI(ctx);
  }

  protected renderUI(ctx: CanvasRenderingContext2D): void {
    if (this.currentPanel === 'main') {
      this.renderLeftSidebar(ctx);
      this.renderActionBar(ctx);
      this.renderTaskGuide(ctx);
      this.renderStatusBar(ctx);
      this.renderInteractionMenu(ctx);

      // 显示邻居来访提示
      if (this.currentGameScene === 'yard' && this.neighborSystem.isNeighborVisiting()) {
        this.renderNeighborNotification(ctx);
      }
    }

    switch (this.currentPanel) {
      case 'status':
        this.renderStatusPanel(ctx);
        break;
      case 'story':
        this.renderStoryPanel(ctx);
        break;
      case 'collection':
        this.renderMemoryPanel(ctx);
        break;
      case 'neighbor':
        this.renderNeighborPanel(ctx);
        break;
      case 'dialogue':
        this.renderDialoguePanel(ctx);
        break;
    }
  }

  private renderLeftSidebar(ctx: CanvasRenderingContext2D): void {
    const isMobile = this.width < 600;
    
    this.leftSidebarButtons.forEach(btn => {
      if (!btn.visible) return;
      if (btn.x + btn.width < 0 || btn.x > this.width || 
          btn.y + btn.height < 0 || btn.y > this.height) {
        return;
      }

      ctx.save();
      
      // 按钮背景渐变
      const gradient = ctx.createLinearGradient(btn.x, btn.y, btn.x, btn.y + btn.height);
      gradient.addColorStop(0, 'rgba(60, 60, 60, 0.85)');
      gradient.addColorStop(1, 'rgba(30, 30, 30, 0.95)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.roundRect(btn.x, btn.y, btn.width, btn.height, isMobile ? 8 : 12);
      ctx.fill();
      
      // 边框
      ctx.strokeStyle = 'rgba(139, 195, 74, 0.4)';
      ctx.lineWidth = isMobile ? 1 : 2;
      ctx.stroke();

      // 图标
      ctx.font = (isMobile ? 18 : 24) + 'px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(btn.icon, btn.x + btn.width / 2, btn.y + btn.height / 2 - (isMobile ? 2 : 4));

      // 标签
      ctx.fillStyle = '#FFF';
      ctx.font = (isMobile ? 8 : 10) + 'px -apple-system, sans-serif';
      ctx.fillText(btn.label, btn.x + btn.width / 2, btn.y + btn.height - (isMobile ? 5 : 8));
      
      ctx.restore();
    });
  }

  private renderActionBar(ctx: CanvasRenderingContext2D): void {
    if (!this.showActionBar) return;

    // 手机端适配 - 更小的按钮，更紧凑的布局
    const isMobile = this.width < 600;
    const barY = isMobile ? this.height - 42 : this.height - 80;
    const btnWidth = isMobile ? 32 : 60;
    const btnHeight = isMobile ? 24 : 50;
    const gap = isMobile ? 2 : 10;
    const fontSize = isMobile ? 12 : 24;
    const labelFontSize = isMobile ? 7 : 12;
    const startX = (this.width - (this.actionBarButtons.length * (btnWidth + gap) - gap)) / 2;

    // 动作栏背景 - 更美观
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.beginPath();
    ctx.roundRect(startX - 10, barY - 8, 
      this.actionBarButtons.length * (btnWidth + gap) + 10, btnHeight + 16, 15);
    ctx.fill();
    
    // 边框
    ctx.strokeStyle = 'rgba(139, 195, 74, 0.3)';
    ctx.lineWidth = 2;
    ctx.stroke();

    this.actionBarButtons.forEach((btn, i) => {
      const btnX = startX + i * (btnWidth + gap);
      const isHovered = this.hoveredActionButton === btn.id;
      const isSelected = this.selectedAction === btn.action;
      
      // 按钮背景
      if (isSelected) {
        ctx.fillStyle = 'rgba(139, 195, 74, 0.9)';
      } else if (isHovered && btn.enabled) {
        ctx.fillStyle = 'rgba(80, 80, 80, 0.95)';
      } else if (btn.enabled) {
        ctx.fillStyle = 'rgba(50, 50, 50, 0.9)';
      } else {
        ctx.fillStyle = 'rgba(30, 30, 30, 0.9)';
      }
      ctx.beginPath();
      ctx.roundRect(btnX, barY, btnWidth, btnHeight, 10);
      ctx.fill();

      // 选中/悬停边框
      if (isSelected || isHovered) {
        ctx.strokeStyle = isSelected ? '#FFF' : 'rgba(139, 195, 74, 0.6)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // 图标
      ctx.font = fontSize + 'px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = btn.enabled ? '#FFF' : '#666';
      ctx.fillText(btn.icon, btnX + btnWidth / 2, barY + btnHeight / 2 - 8);

      // 标签
      ctx.font = labelFontSize + 'px sans-serif';
      ctx.fillStyle = btn.enabled ? '#FFF' : '#666';
      ctx.fillText(btn.label, btnX + btnWidth / 2, barY + btnHeight - 10);
    });
  }

  private renderTaskGuide(ctx: CanvasRenderingContext2D): void {
    if (!this.showTaskGuide) return;

    // 手机端适配 - 更紧凑
    const isMobile = this.width < 600;
    const padding = isMobile ? 8 : 15;
    const fontSize = isMobile ? 10 : 14;
    const boxHeight = isMobile ? 22 : 36;
    
    ctx.font = fontSize + 'px sans-serif';
    const textWidth = ctx.measureText(this.taskGuideHint).width;
    const boxWidth = textWidth + padding * 2;
    const x = (this.width - boxWidth) / 2;
    const y = isMobile ? 8 : 20;

    // 背景
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.beginPath();
    ctx.roundRect(x, y, boxWidth, boxHeight, isMobile ? 12 : 18);
    ctx.fill();

    // 边框
    ctx.strokeStyle = '#8BC34A';
    ctx.lineWidth = isMobile ? 1 : 2;
    ctx.stroke();

    // 文字
    ctx.fillStyle = '#FFF';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.taskGuideHint, this.width / 2, y + boxHeight / 2);
  }

  private renderStatusBar(ctx: CanvasRenderingContext2D): void {
    const huanhuanState = this.actionSystem.getHuanhuanState();
    const isMobile = this.width < 600;
    const barHeight = isMobile ? 22 : 30;
    const barY = isMobile ? 50 : 70;
    const padding = isMobile ? 8 : 15;
    const fontSize = isMobile ? 12 : 16;
    const smallFontSize = isMobile ? 9 : 11;

    // 情感状态 - 手机端隐藏文字只显示表情
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.beginPath();
    ctx.roundRect(padding, barY, isMobile ? 35 : 100, barHeight, isMobile ? 10 : 15);
    ctx.fill();

    const emotionEmoji: Record<string, string> = {
      'happy': '😊', 'tired': '😴', 'curious': '🤔', 
      'sad': '😢', 'excited': '🤩', 'content': '😌'
    };
    ctx.font = fontSize + 'px Arial';
    ctx.textAlign = 'center';
    if (isMobile) {
      ctx.fillText(emotionEmoji[huanhuanState.emotion] || '😐', padding + 17, barY + barHeight / 2 + 2);
    } else {
      ctx.fillText(`${emotionEmoji[huanhuanState.emotion] || '😐'} ${huanhuanState.emotion}`, padding + 50, barY + barHeight / 2 + 2);
    }

    // 能量条
    const energyX = isMobile ? padding + 40 : padding + 110;
    const energyWidth = isMobile ? 70 : 120;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.beginPath();
    ctx.roundRect(energyX, barY, energyWidth, barHeight, isMobile ? 10 : 15);
    ctx.fill();

    ctx.fillStyle = '#333';
    ctx.fillRect(energyX + (isMobile ? 5 : 10), barY + (isMobile ? 6 : 10), energyWidth - (isMobile ? 10 : 20), isMobile ? 8 : 10);
    ctx.fillStyle = huanhuanState.energy > 50 ? '#8BC34A' : (huanhuanState.energy > 20 ? '#FFC107' : '#F44336');
    ctx.fillRect(energyX + (isMobile ? 5 : 10), barY + (isMobile ? 6 : 10), (energyWidth - (isMobile ? 10 : 20)) * (huanhuanState.energy / 100), isMobile ? 8 : 10);

    ctx.fillStyle = '#FFF';
    ctx.font = smallFontSize + 'px sans-serif';
    if (isMobile) {
      ctx.fillText(`${Math.floor(huanhuanState.energy)}%`, energyX + energyWidth / 2, barY + barHeight / 2 + 2);
    } else {
      ctx.fillText(`体力 ${Math.floor(huanhuanState.energy)}%`, energyX + energyWidth / 2, barY + barHeight / 2 + 2);
    }

    // 回忆度 - 手机端简化显示
    const memoryWidth = isMobile ? 90 : 140;
    const memoryX = this.width - memoryWidth - padding;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.beginPath();
    ctx.roundRect(memoryX, barY, memoryWidth, barHeight, isMobile ? 10 : 15);
    ctx.fill();

    ctx.fillStyle = '#FFF';
    ctx.font = (isMobile ? 11 : 12) + 'px sans-serif';
    ctx.textAlign = 'center';
    const stage = this.storySystem.getCurrentGrowthStage();
    if (isMobile) {
      ctx.fillText(`${stage.name}`, memoryX + memoryWidth / 2, barY + barHeight / 2 + 2);
    } else {
      ctx.fillText(`💭 ${stage.name} (${this.storySystem.getTotalMemoryPoints()}点)`, memoryX + memoryWidth / 2, barY + barHeight / 2 + 2);
    }
  }

  private renderInteractionMenu(ctx: CanvasRenderingContext2D): void {
    if (!this.showInteractionMenu) return;

    const menuX = this.interactionMenuPosition.x;
    const menuY = this.interactionMenuPosition.y;
    const menuWidth = 140;
    const menuHeight = 60 + this.interactionMenuOptions.length * 40;
    
    ctx.save();
    
    // 菜单背景
    ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.beginPath();
    ctx.roundRect(menuX - menuWidth/2, menuY, menuWidth, menuHeight, 12);
    ctx.fill();
    
    // 边框
    ctx.strokeStyle = 'rgba(139, 195, 74, 0.6)';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // 标题栏
    ctx.fillStyle = 'rgba(139, 195, 74, 0.3)';
    ctx.beginPath();
    ctx.roundRect(menuX - menuWidth/2 + 3, menuY + 3, menuWidth - 6, 40, 8);
    ctx.fill();
    
    // 关闭按钮
    ctx.fillStyle = '#F44336';
    ctx.beginPath();
    ctx.arc(menuX + menuWidth/2 - 15, menuY + 20, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#FFF';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('×', menuX + menuWidth/2 - 15, menuY + 24);
    
    // 标题
    ctx.fillStyle = '#FFF';
    ctx.font = 'bold 14px sans-serif';
    ctx.fillText(this.interactionMenuTitle, menuX, menuY + 25);
    
    // 选项
    this.interactionMenuOptions.forEach((option, i) => {
      const itemY = menuY + 50 + i * 40;
      const isHovered = this.hoveredInteractionOption === option.id;
      
      // 选项背景
      if (isHovered && option.enabled) {
        ctx.fillStyle = 'rgba(139, 195, 74, 0.4)';
        ctx.beginPath();
        ctx.roundRect(menuX - menuWidth/2 + 5, itemY, menuWidth - 10, 35, 8);
        ctx.fill();
      }
      
      // 图标
      ctx.font = '18px Arial';
      ctx.fillStyle = option.enabled ? '#FFF' : '#666';
      ctx.textAlign = 'left';
      ctx.fillText(option.icon, menuX - menuWidth/2 + 12, itemY + 24);
      
      // 标签
      ctx.font = '13px sans-serif';
      ctx.fillText(option.label, menuX - menuWidth/2 + 35, itemY + 24);
    });
    
    ctx.restore();
  }

  private renderNeighborNotification(ctx: CanvasRenderingContext2D): void {
    const neighbor = this.neighborSystem.getCurrentNeighbor();
    if (!neighbor) return;

    const x = this.width / 2;
    const y = 120;

    ctx.save();
    ctx.fillStyle = 'rgba(139, 195, 74, 0.9)';
    ctx.beginPath();
    ctx.roundRect(x - 120, y - 20, 240, 40, 20);
    ctx.fill();
    
    // 边框
    ctx.strokeStyle = '#FFF';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = '#FFF';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${neighbor.avatar} ${neighbor.name}正在做客，点击互动`, x, y + 5);
    ctx.restore();
  }

  private renderStatusPanel(ctx: CanvasRenderingContext2D): void {
    const w = this.width;
    const h = this.height;
    const panelW = Math.min(600, w - 40);
    const panelH = Math.min(500, h - 100);
    const x = (w - panelW) / 2;
    const y = (h - panelH) / 2;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.beginPath();
    ctx.roundRect(x, y, panelW, panelH, 16);
    ctx.fill();

    ctx.strokeStyle = '#8BC34A';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.fillStyle = '#FFF';
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('👤 角色状态', w / 2, y + 40);

    const huanhuanState = this.actionSystem.getHuanhuanState();
    const dandanState = this.actionSystem.getDandanState();
    const grandmaState = this.actionSystem.getGrandmaState();
    const dayeState = this.actionSystem.getDayeState();
    const gameTime = this.weatherEventSystem.getGameTime();

    ctx.textAlign = 'left';
    ctx.font = '18px sans-serif';

    let lineY = y + 80;
    const lineHeight = 35;

    // 欢欢状态
    ctx.fillStyle = '#FFD700';
    ctx.fillText(`名字: ${this.gameState.get('playerName')}`, x + 30, lineY);
    lineY += lineHeight;
    ctx.fillText(`年龄: ${this.gameState.getCurrentAge()}岁`, x + 30, lineY);
    lineY += lineHeight;
    ctx.fillText(`情感: ${huanhuanState.emotion}`, x + 30, lineY);
    lineY += lineHeight;
    ctx.fillText(`体力: ${Math.floor(huanhuanState.energy)}%`, x + 30, lineY);
    lineY += lineHeight + 10;

    // 丹丹状态
    ctx.fillStyle = '#D2691E';
    ctx.fillText(`🐕 丹丹心情: ${dandanState.mood}`, x + 30, lineY);
    lineY += lineHeight;
    ctx.fillText(`🐕 丹丹活动: ${dandanState.activity}`, x + 30, lineY);
    lineY += lineHeight + 10;

    // 家人状态
    ctx.fillStyle = '#8BC34A';
    ctx.fillText(`👵 奶奶正在: ${grandmaState.currentActivity}`, x + 30, lineY);
    lineY += lineHeight;
    ctx.fillText(`👴 爷爷正在: ${dayeState.currentActivity}`, x + 30, lineY);
    lineY += lineHeight + 10;

    // 游戏时间
    ctx.fillStyle = '#9370DB';
    ctx.fillText(`🕐 第${gameTime.day}天 ${String(gameTime.hour).padStart(2, '0')}:${String(gameTime.minute).padStart(2, '0')}`, x + 30, lineY);
    lineY += lineHeight;

    // 回忆进度
    ctx.fillStyle = '#E1BEE7';
    ctx.fillText(`💭 回忆度: ${this.storySystem.getTotalMemoryPoints()}点`, x + 30, lineY);
    lineY += lineHeight;

    const stage = this.storySystem.getCurrentGrowthStage();
    ctx.fillText(`🌟 成长阶段: ${stage.name}`, x + 30, lineY);

    ctx.fillStyle = '#AAA';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('点击任意处关闭', w / 2, y + panelH - 20);
  }

  private renderStoryPanel(ctx: CanvasRenderingContext2D): void {
    const w = this.width;
    const h = this.height;
    const panelW = Math.min(600, w - 40);
    const panelH = Math.min(500, h - 100);
    const x = (w - panelW) / 2;
    const y = (h - panelH) / 2;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.95)';
    ctx.beginPath();
    ctx.roundRect(x, y, panelW, panelH, 16);
    ctx.fill();

    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.fillStyle = '#FFF';
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('📖 任务与故事', w / 2, y + 40);

    const currentTask = this.storySystem.getCurrentTask();
    
    ctx.textAlign = 'left';
    ctx.font = '16px sans-serif';
    let lineY = y + 80;

    if (currentTask) {
      ctx.fillStyle = '#FFD700';
      ctx.fillText(`当前任务: ${currentTask.title}`, x + 30, lineY);
      lineY += 30;
      
      ctx.fillStyle = '#AAA';
      ctx.font = '14px sans-serif';
      ctx.fillText(currentTask.description, x + 30, lineY);
      lineY += 40;

      ctx.font = '14px sans-serif';
      currentTask.objectives.forEach(obj => {
        const color = obj.current >= obj.required ? '#8BC34A' : '#FFF';
        ctx.fillStyle = color;
        const status = obj.current >= obj.required ? '✓' : '○';
        ctx.fillText(`${status} ${obj.description} (${obj.current}/${obj.required})`, x + 30, lineY);
        lineY += 25;
      });
    } else {
      ctx.fillStyle = '#8BC34A';
      ctx.fillText('✨ 所有主线任务已完成！', x + 30, lineY);
    }

    // 成长阶段
    lineY += 30;
    ctx.fillStyle = '#9C27B0';
    ctx.font = 'bold 16px sans-serif';
    ctx.fillText('🌟 成长阶段', x + 30, lineY);
    lineY += 30;

    const stage = this.storySystem.getCurrentGrowthStage();
    const nextStage = this.storySystem.getNextGrowthStage();
    const progress = this.storySystem.getProgressToNextStage();

    ctx.fillStyle = '#E1BEE7';
    ctx.font = '14px sans-serif';
    ctx.fillText(`当前: ${stage.name}`, x + 30, lineY);
    lineY += 25;
    ctx.fillText(`描述: ${stage.description}`, x + 30, lineY);
    lineY += 25;

    // 进度条
    ctx.fillStyle = '#333';
    ctx.fillRect(x + 30, lineY, 200, 15);
    ctx.fillStyle = '#9C27B0';
    ctx.fillRect(x + 30, lineY, 200 * (progress / 100), 15);
    lineY += 25;

    if (nextStage) {
      ctx.fillStyle = '#B39DDB';
      ctx.fillText(`下一级: ${nextStage.name} (${Math.floor(progress)}%)`, x + 30, lineY);
    }

    ctx.fillStyle = '#AAA';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('点击任意处关闭', w / 2, y + panelH - 20);
  }

  private renderMemoryPanel(ctx: CanvasRenderingContext2D): void {
    const w = this.width;
    const h = this.height;
    const panelW = Math.min(600, w - 40);
    const panelH = Math.min(500, h - 100);
    const x = (w - panelW) / 2;
    const y = (h - panelH) / 2;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.95)';
    ctx.beginPath();
    ctx.roundRect(x, y, panelW, panelH, 16);
    ctx.fill();

    ctx.strokeStyle = '#9C27B0';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.fillStyle = '#FFF';

    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('💭 回忆收集', w / 2, y + 40);

    const fragments = this.storySystem.getUnlockedFragments();
    const allFragments = this.storySystem.getAllFragments();

    ctx.fillStyle = '#E1BEE7';
    ctx.font = '16px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`收集进度: ${fragments.length}/${allFragments.length}`, w / 2, y + 70);

    ctx.textAlign = 'left';
    ctx.font = '14px sans-serif';
    let lineY = y + 100;

    if (fragments.length === 0) {
      ctx.fillStyle = '#888';
      ctx.textAlign = 'center';
      ctx.fillText('还没有收集到任何回忆...', w / 2, y + 200);
      ctx.fillText('多和家人对话，触发回忆吧！', w / 2, y + 230);
    } else {
      fragments.forEach(frag => {
        ctx.fillStyle = 'rgba(156, 39, 176, 0.3)';
        ctx.beginPath();
        ctx.roundRect(x + 20, lineY - 20, panelW - 40, 60, 10);
        ctx.fill();

        ctx.fillStyle = '#E1BEE7';
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText(`💭 ${frag.title}`, x + 35, lineY);

        ctx.fillStyle = '#B39DDB';
        ctx.font = '12px sans-serif';
        const truncated = frag.content.length > 40 ? frag.content.slice(0, 40) + '...' : frag.content;
        ctx.fillText(truncated, x + 35, lineY + 18);

        ctx.fillStyle = '#9575CD';
        ctx.fillText(`来源: ${frag.source} | +${frag.memoryPoints}回忆度`, x + 35, lineY + 35);

        lineY += 70;
      });
    }

    ctx.fillStyle = '#AAA';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('点击任意处关闭', w / 2, y + panelH - 20);
  }

  private renderNeighborPanel(ctx: CanvasRenderingContext2D): void {
    // 复用原有的邻居面板渲染
    const w = this.width;

    if (this.neighborSystem.isNeighborVisiting()) {
      const neighbor = this.neighborSystem.getCurrentNeighbor();
      if (!neighbor) return;

      const panelW = Math.min(500, w - 40);
      const panelH = 250;
      const px = (w - panelW) / 2;
      const py = this.height - panelH - 20;

      ctx.fillStyle = 'rgba(0, 0, 0, 0.95)';
      ctx.beginPath();
      ctx.roundRect(px, py, panelW, panelH, 16);
      ctx.fill();

      ctx.strokeStyle = '#8BC34A';
      ctx.lineWidth = 3;
      ctx.stroke();

      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 20px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`${neighbor.avatar} ${neighbor.name}`, px + 20, py + 35);

      const friendship = this.neighborSystem.getFriendship(neighbor.id);
      const level = this.neighborSystem.getFriendshipLevel(neighbor.id);
      ctx.fillStyle = level.color;
      ctx.font = '14px sans-serif';
      ctx.fillText(`好感度: ${friendship} (${level.name})`, px + panelW - 150, py + 35);

      const dialogue = this.neighborSystem.getCurrentDialogue();
      ctx.fillStyle = '#FFF';
      ctx.font = '16px sans-serif';

      const maxWidth = panelW - 40;
      const lineHeight = 24;
      const words = dialogue.split('');
      let line = '';
      let lineY = py + 75;

      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i];
        const metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth && i > 0) {
          ctx.fillText(line, px + 20, lineY);
          line = words[i];
          lineY += lineHeight;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line, px + 20, lineY);

      const options = this.neighborSystem.getDialogueOptions();
      let optionY = py + 130;

      options.forEach((option, i) => {
        ctx.fillStyle = 'rgba(139, 195, 74, 0.3)';
        ctx.beginPath();
        ctx.roundRect(px + 20, optionY - 18, panelW - 40, 28, 6);
        ctx.fill();

        ctx.fillStyle = '#8BC34A';
        ctx.fillText(`${i + 1}. ${option}`, px + 30, optionY);
        optionY += 35;
      });
    }
  }

  private renderDialoguePanel(ctx: CanvasRenderingContext2D): void {
    if (!this.showDialogue) return;

    const w = this.width;
    const h = this.height;
    const boxH = 220;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.95)';
    ctx.beginPath();
    ctx.roundRect(20, h - boxH - 20, w - 40, boxH, 16);
    ctx.fill();

    ctx.strokeStyle = '#8BC34A';
    ctx.lineWidth = 3;
    ctx.stroke();

    if (this.dialogueSpeaker) {
      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 20px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(this.dialogueSpeaker, 40, h - boxH + 25);
    }

    ctx.fillStyle = '#FFF';
    ctx.font = '18px sans-serif';

    const maxWidth = w - 80;
    const lineHeight = 28;
    const words = this.dialogueText.split('');
    let line = '';
    let y = h - boxH + 65;

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i];
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxWidth && i > 0) {
        ctx.fillText(line, 40, y);
        line = words[i];
        y += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, 40, y);

    if (this.dialogueChoices.length > 0 && this.typingProgress >= this.targetText.length) {
      y += 35;
      this.dialogueChoices.forEach((choice, i) => {
        ctx.fillStyle = 'rgba(139, 195, 74, 0.3)';
        ctx.beginPath();
        ctx.roundRect(35, y - 20, w - 70, 28, 6);
        ctx.fill();

        ctx.fillStyle = '#8BC34A';
        ctx.fillText(`${i + 1}. ${choice.text}`, 40, y);
        y += 35;
      });
    }

    if (this.typingProgress >= this.targetText.length && this.dialogueChoices.length === 0) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('点击继续', w / 2, h - 40);
    }
  }

  // 公共方法
  public getGameState(): GameState { return this.gameState; }
  public getInventorySystem(): InventorySystem { return this.inventorySystem; }
  public getCurrentPanel(): UIPanel { return this.currentPanel; }
  public getStorySystem(): StorySystem { return this.storySystem; }
  public getActionSystem(): ActionSystem { return this.actionSystem; }
  public getCurrentScene(): GameScene { return this.currentGameScene; }
  public setCurrentScene(scene: GameScene): void { this.currentGameScene = scene; }
  
  public toggleSettings(): void {
    if (this.currentPanel === 'settings') {
      this.closePanel();
    } else {
      this.openPanel('settings');
    }
  }
}